import moment from "moment-timezone";
import type { FilterRow } from "components/shared/filters/Filters/types";

export const DEPARTMENTS = ["Front of House", "Kitchen", "Housekeeping", "Maintenance", "Guest Services"];
export const UNITS = ["Downtown", "Airport", "Riverside", "Uptown", "Harbor"];

export type WindowStatus = "draft" | "scheduled" | "open" | "closed";

export interface SeniorityTier {
    id: string;
    label: string;
    minSeniorityYears: number;
    accessOffsetHours: number;
    maxShiftsPerEmployee: number;
}

export interface SelfSchedulingWindow {
    id: string;
    name: string;
    department: string;
    unit: string;
    targetPeriodFrom: string;
    targetPeriodTo: string;
    openDate: string;
    closeDate: string;
    published: boolean;
    shiftIds: string[];
    tiers: SeniorityTier[];
}

const pad = (value: number) => String(value).padStart(2, "0");

const seedHash = (seed: string) => {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
    }
    return hash;
};

export const getWindowStatus = (window: Pick<SelfSchedulingWindow, "published" | "openDate" | "closeDate">): WindowStatus => {
    if (!window.published) return "draft";
    const today = moment().startOf("day");
    if (today.isBefore(moment(window.openDate), "day")) return "scheduled";
    if (today.isAfter(moment(window.closeDate), "day")) return "closed";
    return "open";
};

export interface ShiftBreak {
    start: string;
    end: string;
}

export type HourType = "regular" | "overtime" | "pto" | "holiday" | "unpaid";

export interface PayCode {
    code: string;
    description: string;
    hourType: HourType;
}

// Mirrors getHourTypeColor from Component/components/drawers/Event/Form/components/PayCodeSelect.
export const HOUR_TYPE_COLORS: Record<HourType, string> = {
    regular: "#3B82F6",
    overtime: "#F59E0B",
    pto: "#10B981",
    holiday: "#8B5CF6",
    unpaid: "#94A3B8",
};

export const PAY_CODES: PayCode[] = [
    { code: "REG", description: "Regular", hourType: "regular" },
    { code: "OT", description: "Overtime", hourType: "overtime" },
    { code: "HOL", description: "Holiday", hourType: "holiday" },
    { code: "PTO", description: "Paid time off", hourType: "pto" },
];

// An "unassigned shift" is a shift a manager created without picking an employee for it —
// the same underlying concept the real /unassigned-shifts endpoint manages. A self-scheduling
// window doesn't invent its own shifts, it just points at a set of these by id.
export interface UnassignedShift {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
    role: string;
    payCode: string;
    location: string;
    department: string;
    unit: string;
    slots: number;
    breaks: ShiftBreak[];
}

export const ROLES_BY_DEPARTMENT: Record<string, string[]> = {
    "Front of House": ["Host", "Server", "Bartender"],
    "Kitchen": ["Line Cook", "Prep Cook", "Dishwasher"],
    "Housekeeping": ["Room Attendant", "Laundry Attendant"],
    "Maintenance": ["Maintenance Tech"],
    "Guest Services": ["Concierge", "Front Desk Agent"],
};

// The real form scopes job/location choices to the assigned employee, but an unassigned shift
// has no employee to scope by, so it offers the full company-wide job catalog instead.
export const ALL_ROLES: string[] = Array.from(new Set(Object.values(ROLES_BY_DEPARTMENT).flat()));

const SHIFT_START_HOURS = [6, 7, 14, 15, 22, 23];

const generateUnassignedShifts = (count = 400): UnassignedShift[] =>
    Array.from({ length: count }, (_, index) => {
        const hash = seedHash(`unassigned-shift-${index}`);
        const department = DEPARTMENTS[hash % DEPARTMENTS.length];
        const unit = UNITS[(hash >> 2) % UNITS.length];
        const roles = ROLES_BY_DEPARTMENT[department] ?? ["Staff"];
        const startHour = SHIFT_START_HOURS[hash % SHIFT_START_HOURS.length];
        const durationHours = 6 + (hash % 3) * 2;
        const date = moment().add((hash % 45) - 10, "days").format("YYYY-MM-DD");

        return {
            id: `unassigned-shift-${index}`,
            date,
            startTime: `${pad(startHour)}:00`,
            endTime: `${pad((startHour + durationHours) % 24)}:00`,
            role: roles[hash % roles.length],
            payCode: PAY_CODES[0].code,
            location: unit,
            department,
            unit,
            slots: 1 + (hash % 3),
            breaks: [],
        };
    });

export const UNASSIGNED_SHIFTS: UnassignedShift[] = generateUnassignedShifts();

export const getUnassignedShiftsFor = (department: string, unit: string, from?: string, to?: string): UnassignedShift[] =>
    UNASSIGNED_SHIFTS.filter((shift) => (
        shift.department === department
        && shift.unit === unit
        && (!from || !to || moment(shift.date).isBetween(from, to, "day", "[]"))
    ));

export const createDraftUnassignedShift = (): UnassignedShift => ({
    id: "",
    date: moment().add(7, "days").format("YYYY-MM-DD"),
    startTime: "09:00",
    endTime: "17:00",
    role: ALL_ROLES[0],
    payCode: PAY_CODES[0].code,
    location: UNITS[0],
    department: DEPARTMENTS[0],
    unit: UNITS[0],
    slots: 1,
    breaks: [],
});

// Same overnight-rollover assumption the real form's duration calculation makes: if end isn't
// after start, the shift is treated as crossing into the next day.
export const getSpanSeconds = (startTime: string, endTime: string) => {
    const start = moment(startTime, "HH:mm");
    let end = moment(endTime, "HH:mm");
    if (!end.isAfter(start)) end = end.add(1, "day");
    return end.diff(start, "seconds");
};

export const getBreaksSeconds = (breaks: ShiftBreak[]) =>
    breaks.reduce((total, brk) => total + Math.max(0, getSpanSeconds(brk.start, brk.end)), 0);

export const getPaidSeconds = (shift: Pick<UnassignedShift, "startTime" | "endTime" | "breaks">) =>
    Math.max(0, getSpanSeconds(shift.startTime, shift.endTime) - getBreaksSeconds(shift.breaks));

// Mutates the shared pool in place (rather than returning a new array) so every already-open
// view — the shift list and any window's shift picker — sees the new shift without needing a
// separate sync mechanism; each reads UNASSIGNED_SHIFTS live whenever it recomputes.
export const addUnassignedShift = (shift: Omit<UnassignedShift, "id">): UnassignedShift => {
    const created: UnassignedShift = { ...shift, id: `unassigned-shift-${Date.now()}` };
    UNASSIGNED_SHIFTS.push(created);
    return created;
};

export const editUnassignedShift = (shiftId: string, updates: Omit<UnassignedShift, "id">): UnassignedShift | undefined => {
    const index = UNASSIGNED_SHIFTS.findIndex((shift) => shift.id === shiftId);
    if (index === -1) return undefined;

    const updated: UnassignedShift = { ...updates, id: shiftId };
    UNASSIGNED_SHIFTS[index] = updated;
    return updated;
};

export const removeUnassignedShift = (shiftId: string) => {
    const index = UNASSIGNED_SHIFTS.findIndex((shift) => shift.id === shiftId);
    if (index !== -1) UNASSIGNED_SHIFTS.splice(index, 1);
};

export const getWindowForShift = (shiftId: string, windows: SelfSchedulingWindow[]): SelfSchedulingWindow | undefined =>
    windows.find((window) => window.shiftIds.includes(shiftId));

export const applyUnassignedShiftsFilters = (shifts: UnassignedShift[], filters: FilterRow[], windows: SelfSchedulingWindow[]): UnassignedShift[] =>
    shifts.filter((shift) => filters.every((filter) => {
        if (!filter.name || filter.value === undefined || filter.value === null || filter.value === "") return true;

        const raw = filter.name === "status"
            ? (getWindowForShift(shift.id, windows) ? "assigned" : "unassigned")
            : String((shift as unknown as Record<string, unknown>)[filter.name] ?? "").toLowerCase();
        const values = Array.isArray(filter.value) ? filter.value : [filter.value];

        return values.some((value) => {
            const term = String(typeof value === "object" ? value.id : value).toLowerCase();
            return filter.method === "contains" ? raw.includes(term) : raw === term;
        });
    }));

export const createDraftTier = (index: number): SeniorityTier => ({
    id: `tier-${Date.now()}-${index}`,
    label: `Tier ${index + 1}`,
    minSeniorityYears: 0,
    accessOffsetHours: index * 24,
    maxShiftsPerEmployee: 5,
});

export const createDraftWindow = (): SelfSchedulingWindow => ({
    id: `window-${Date.now()}`,
    name: "",
    department: DEPARTMENTS[0],
    unit: UNITS[0],
    targetPeriodFrom: moment().add(14, "days").format("YYYY-MM-DD"),
    targetPeriodTo: moment().add(20, "days").format("YYYY-MM-DD"),
    openDate: moment().add(1, "day").format("YYYY-MM-DD"),
    closeDate: moment().add(7, "days").format("YYYY-MM-DD"),
    published: false,
    shiftIds: [],
    tiers: [createDraftTier(0)],
});

// A duplicate keeps the rules (department/unit/tiers/dates) but not the shifts — those
// already belong to the original window, and a shift can't be claimable through two
// windows at once (see generateSelfSchedulingWindows / the picker's "in another window" guard).
export const duplicateWindow = (window: SelfSchedulingWindow, copySuffix: string): SelfSchedulingWindow => ({
    ...window,
    id: `window-${Date.now()}`,
    name: `${window.name} ${copySuffix}`.trim(),
    published: false,
    shiftIds: [],
    tiers: window.tiers.map((tier, index) => ({ ...tier, id: `tier-${Date.now()}-${index}` })),
});

const WINDOW_NAMES = ["Week", "Pay Period", "Rotation", "Sprint"];

const TIER_LABELS = ["Senior staff", "Standard staff", "New hires"];

const generateTiers = (seed: string): SeniorityTier[] => {
    const hash = seedHash(seed);
    const tierCount = 1 + (hash % 3);
    return Array.from({ length: tierCount }, (_, index) => {
        const tierHash = seedHash(`${seed}-tier-${index}`);
        return {
            id: `${seed}-tier-${index}`,
            label: TIER_LABELS[index] ?? `Tier ${index + 1}`,
            minSeniorityYears: Math.max(0, (tierCount - index - 1) * 2 + (tierHash % 2)),
            accessOffsetHours: index * (12 + (tierHash % 12)),
            maxShiftsPerEmployee: 3 + (tierHash % 5),
        };
    });
};

export const generateSelfSchedulingWindows = (count = 16): SelfSchedulingWindow[] => {
    // A shift already claimed by one window is excluded from every later window, so no
    // shift ends up claimable through two different windows at once.
    const takenShiftIds = new Set<string>();

    return Array.from({ length: count }, (_, index) => {
        const seed = `window-${index}`;
        const hash = seedHash(seed);
        const department = DEPARTMENTS[hash % DEPARTMENTS.length];
        const unit = UNITS[(hash >> 2) % UNITS.length];

        // Spread windows across past, currently-open, and future so mock statuses are well represented.
        const openOffsetDays = ((hash % 40) - 20);
        const openDate = moment().add(openOffsetDays, "days").format("YYYY-MM-DD");
        const closeDate = moment(openDate).add(3 + (hash % 5), "days").format("YYYY-MM-DD");
        const targetPeriodFrom = moment(closeDate).add(3, "days").format("YYYY-MM-DD");
        const targetPeriodTo = moment(targetPeriodFrom).add(6, "days").format("YYYY-MM-DD");

        // Only shifts that actually fall within this window's own target period are eligible —
        // a window shouldn't be able to claim a shift dated outside the period it's scheduling for.
        const available = getUnassignedShiftsFor(department, unit, targetPeriodFrom, targetPeriodTo)
            .filter((shift) => !takenShiftIds.has(shift.id));
        const shiftCount = Math.max(0, Math.min(available.length, 2 + (hash % 5)));
        const shiftIds = available.slice(0, shiftCount).map((shift) => shift.id);
        shiftIds.forEach((id) => takenShiftIds.add(id));

        return {
            id: seed,
            name: `${WINDOW_NAMES[hash % WINDOW_NAMES.length]} ${moment(targetPeriodFrom).format("MMM D")} — ${unit}`,
            department,
            unit,
            targetPeriodFrom,
            targetPeriodTo,
            openDate,
            closeDate,
            published: hash % 9 !== 0,
            shiftIds,
            tiers: generateTiers(seed),
        };
    });
};

export interface SelfSchedulingShift {
    id: string;
    windowId: string;
    date: string;
    startTime: string;
    endTime: string;
    role: string;
    location: string;
    department: string;
    unit: string;
    durationHours: number;
    status: "open" | "claimed";
    claimedBy?: { id: string; name: string };
}

export interface MockEmployee {
    id: string;
    name: string;
    department: string;
    unit: string;
    seniorityYears: number;
}

export const MOCK_EMPLOYEES: MockEmployee[] = [
    { id: "emp-0", name: "Arben Hoxha", department: DEPARTMENTS[0], unit: UNITS[0], seniorityYears: 9 },
    { id: "emp-1", name: "Elira Krasniqi", department: DEPARTMENTS[0], unit: UNITS[0], seniorityYears: 2 },
    { id: "emp-2", name: "Dritan Berisha", department: DEPARTMENTS[1], unit: UNITS[1], seniorityYears: 5 },
    { id: "emp-3", name: "Vesa Shala", department: DEPARTMENTS[1], unit: UNITS[1], seniorityYears: 0 },
    { id: "emp-4", name: "Ilir Kelmendi", department: DEPARTMENTS[2], unit: UNITS[2], seniorityYears: 12 },
    { id: "emp-5", name: "Blerta Gashi", department: DEPARTMENTS[2], unit: UNITS[2], seniorityYears: 1 },
];

const parseHour = (time: string) => Number(time.split(":")[0]);

export const generateShiftsForWindow = (window: SelfSchedulingWindow): SelfSchedulingShift[] => {
    const shifts = window.shiftIds
        .map((shiftId) => UNASSIGNED_SHIFTS.find((shift) => shift.id === shiftId))
        .filter((shift): shift is UnassignedShift => Boolean(shift));

    return shifts.flatMap((shift) => Array.from({ length: shift.slots }, (_, slotIndex) => {
        const hash = seedHash(`${window.id}-${shift.id}-slot-${slotIndex}`);
        // A third of slots start out pre-claimed so the demo shows a mix of open/claimed shifts.
        const isClaimed = hash % 3 === 0;
        const claimant = isClaimed ? MOCK_EMPLOYEES[hash % MOCK_EMPLOYEES.length] : undefined;

        return {
            id: `${shift.id}-slot-${slotIndex}`,
            windowId: window.id,
            date: shift.date,
            startTime: shift.startTime,
            endTime: shift.endTime,
            role: shift.role,
            location: shift.location,
            department: shift.department,
            unit: shift.unit,
            durationHours: (parseHour(shift.endTime) - parseHour(shift.startTime) + 24) % 24,
            status: isClaimed ? "claimed" as const : "open" as const,
            claimedBy: claimant ? { id: claimant.id, name: claimant.name } : undefined,
        };
    }));
};

// Layers live claims (from the shared claimsStore) on top of a shift list's seeded
// base state — shared by the employee claim view and the manager's claim-activity view
// so both read the same override logic instead of drifting apart.
export const applyClaimOverrides = (
    shifts: SelfSchedulingShift[],
    claims: Record<string, { id: string; name: string }>,
): SelfSchedulingShift[] =>
    shifts.map((shift) => (claims[shift.id] ? { ...shift, status: "claimed" as const, claimedBy: claims[shift.id] } : shift));

export interface TierAccess {
    tier: SeniorityTier | null;
    accessAt: string;
    hasAccess: boolean;
}

export const getEmployeeTierAccess = (window: SelfSchedulingWindow, employee: MockEmployee): TierAccess => {
    const sortedTiers = [...window.tiers].sort((a, b) => b.minSeniorityYears - a.minSeniorityYears);
    const tier = sortedTiers.find((t) => employee.seniorityYears >= t.minSeniorityYears) ?? sortedTiers[sortedTiers.length - 1] ?? null;

    if (!tier) return { tier: null, accessAt: window.openDate, hasAccess: false };

    const accessAt = moment(window.openDate).add(tier.accessOffsetHours, "hours");
    return { tier, accessAt: accessAt.toISOString(), hasAccess: moment().isSameOrAfter(accessAt) };
};

export const applySelfSchedulingFilters = (windows: SelfSchedulingWindow[], filters: FilterRow[]): SelfSchedulingWindow[] =>
    windows.filter((window) => filters.every((filter) => {
        if (!filter.name || filter.value === undefined || filter.value === null || filter.value === "") return true;

        const raw = filter.name === "status"
            ? getWindowStatus(window)
            : String((window as unknown as Record<string, unknown>)[filter.name] ?? "").toLowerCase();
        const values = Array.isArray(filter.value) ? filter.value : [filter.value];

        return values.some((value) => {
            const term = String(typeof value === "object" ? value.id : value).toLowerCase();
            return filter.method === "contains" ? raw.toLowerCase().includes(term) : raw.toLowerCase() === term;
        });
    }));
